import React from 'react';
import {
	Control,
	FieldErrors,
	UseFormSetValue,
	UseFormWatch,
} from 'react-hook-form';

import { Checkbox, Label, Meta, Select } from '@components/common';
import { IFieldsAlarmManagementForm } from '@components/ModalComponents/AlarmManagementForm/AlarmManagementForm';

import { TSelectOption } from '@interfaces/components/componentTypes';

import { TUserRoles } from '@enums/common';
import { EAlarmManagementFields } from '@components/ModalComponents/AlarmManagementForm/enums';

import styles from './Channel.module.scss';
import stylesLabel from '@components/ModalComponents/AlarmManagementForm/AlarmManagementForm.module.scss';

interface IChannelProps {
	watch: UseFormWatch<IFieldsAlarmManagementForm>;
	setValue: UseFormSetValue<IFieldsAlarmManagementForm>;
	errors: FieldErrors<IFieldsAlarmManagementForm>;
	setErrorsChannel: () => void;
	clearErrorsChannel: () => void;
	rolesOptions: TSelectOption<TUserRoles>[];
	usersOptions: TSelectOption<string>[];
	setErrorsRecipients: () => void;
	clearErrorsRecipients: () => void;
	control: Control;
}

const Channel = ({
	watch,
	setValue,
	errors,
	setErrorsChannel,
	clearErrorsChannel,
	rolesOptions,
	usersOptions,
	setErrorsRecipients,
	clearErrorsRecipients,
	control,
}: IChannelProps) => {
	const isEmail = watch(EAlarmManagementFields.isEmail);
	const isNotification = watch(EAlarmManagementFields.isNotification);
	const roles = watch(EAlarmManagementFields.roles);
	const users = watch(EAlarmManagementFields.users);
	const isChannelError = errors.isEmail?.message || errors.isNotification?.message;
	const isRecipientError = errors.users?.message || errors.roles?.message;

	const onChangeIsEmail = () => {
		if (isEmail && !isNotification) setErrorsChannel();
		else clearErrorsChannel();

		setValue(EAlarmManagementFields.isEmail, !isEmail);
	};

	const onChangeIsNotification = () => {
		if (!isEmail && isNotification) setErrorsChannel();
		else clearErrorsChannel();

		setValue(EAlarmManagementFields.isNotification, !isNotification);
	};

	const onChangeUsers = (options: TSelectOption<string>[]) => {
		setValue(EAlarmManagementFields.users, options);

		if (!options?.length && !roles?.length) setErrorsRecipients();
		else clearErrorsRecipients();
	};

	const onChangeRoles = (options: TSelectOption<TUserRoles>[]) => {
		setValue(EAlarmManagementFields.roles, options);

		if (!users?.length && !options?.length) setErrorsRecipients();
		else clearErrorsRecipients();
	};

	return (
		<div className={styles.channelContainer}>
			<div className={styles.checkboxSection}>
				<div>
					<Label
						className={stylesLabel.label}
						label='Channel'
						required
					/>
					<Checkbox
						checked={isEmail}
						isError={!!errors.isEmail}
						label='Email'
						name={EAlarmManagementFields.isEmail}
						onChange={onChangeIsEmail}
					/>
				</div>
				<Checkbox
					checked={isNotification}
					isError={!!errors.isNotification}
					label='Notification'
					name={EAlarmManagementFields.isNotification}
					onChange={onChangeIsNotification}
				/>
				{isChannelError && <Meta message={errors.isEmail?.message || errors.isNotification?.message} />}
			</div>
			<div className={styles.selectSection}>
				<Label
					className={stylesLabel.label}
					label='Recipient'
					required
				/>
				<div className={styles.recipients}>
					<div className={styles.selectWrapper}>
						<Select
							closeMenuOnSelect={false}
							control={control}
							handleChange={onChangeRoles}
							isError={!!errors.roles}
							isMulti
							label='Roles'
							name={EAlarmManagementFields.roles}
							options={rolesOptions}
							value={roles}
						/>
					</div>
					<div className={styles.selectWrapper}>
						<Select
							closeMenuOnSelect={false}
							control={control}
							handleChange={onChangeUsers}
							isError={!!errors.users}
							isMulti
							label='Users'
							name={EAlarmManagementFields.users}
							options={usersOptions}
							value={users}
						/>
					</div>
				</div>
				{isRecipientError && <Meta message={errors.users?.message || errors.roles?.message} />}
			</div>
		</div>
	);
};

export default Channel;
